import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, ArrowRight, Trophy, Users } from "lucide-react";
import api from "@/lib/api";
import CosmicField from "@/components/CosmicField";
import AboutAtlas from "@/components/AboutAtlas";
import HowItWorks from "@/components/HowItWorks";

/**
 * Full narrative / marketing page. Lives at /about.
 * The root landing (/) is now a stripped-down signup + pay-portal card;
 * this page is where prospective users can dig into the story, mechanics,
 * and top-inviter leaderboard before deciding to sign up.
 */
export default function About() {
    const [leaderboard, setLeaderboard] = React.useState([]);

    useEffect(() => {
        api.get("/referrals/leaderboard?limit=6")
            .then((r) => setLeaderboard(r.data || []))
            .catch(() => {});
    }, []);

    return (
        <div className="relative min-h-screen w-full cosmic-sky overflow-hidden">
            <CosmicField count={80} />

            {/* Central node cluster — same visual signature as the landing */}
            <div className="absolute inset-0 flex items-start justify-center pointer-events-none pt-24">
                <div className="relative w-[520px] h-[520px]">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                        <motion.div
                            key={i}
                            className="absolute w-3 h-3 rounded-full"
                            style={{
                                left: `${50 + 34 * Math.cos((i / 6) * Math.PI * 2)}%`,
                                top: `${50 + 34 * Math.sin((i / 6) * Math.PI * 2)}%`,
                                background: ["#FF2052", "#FFC107", "#3EB489", "#6F00FF", "#FFFFFF", "#8BCDF8"][i],
                                boxShadow: `0 0 24px ${["#FF2052", "#FFC107", "#3EB489", "#6F00FF", "#FFFFFF", "#8BCDF8"][i]}`,
                            }}
                            initial={{ scale: 0 }}
                            animate={{ scale: [1, 1.15, 1] }}
                            transition={{ delay: 0.2 + i * 0.08, duration: 3, repeat: Infinity, repeatType: "reverse" }}
                        />
                    ))}
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <line
                                key={i}
                                x1="50"
                                y1="50"
                                x2={50 + 34 * Math.cos((i / 6) * Math.PI * 2)}
                                y2={50 + 34 * Math.sin((i / 6) * Math.PI * 2)}
                                stroke="rgba(255,255,255,0.35)"
                                strokeWidth="0.4"
                                strokeDasharray="0.6 0.8"
                            />
                        ))}
                    </svg>
                    <motion.div
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                        animate={{ scale: [1, 1.08, 1] }}
                        transition={{ duration: 3, repeat: Infinity }}
                    >
                        <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur flex items-center justify-center">
                            <Compass className="w-8 h-8 text-humanities" />
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Hero */}
            <div className="relative z-10 flex flex-col items-center justify-center px-6 py-24 sm:py-32 text-center">
                <p
                    className="chip mx-auto mb-6"
                    style={{ background: "rgba(255,255,255,0.35)", color: "#0A2540" }}
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-arts" /> About Project Atlas
                </p>
                <h1
                    className="font-display font-black text-5xl sm:text-6xl md:text-7xl tracking-tighter text-white leading-[0.95]"
                    style={{ textShadow: "0 4px 40px rgba(10,37,64,0.4)" }}
                    data-testid="about-hero-title"
                >
                    The story behind Atlas.
                </h1>
                <p
                    className="text-white/95 text-xl md:text-2xl mt-6 max-w-3xl mx-auto leading-relaxed"
                    style={{ textShadow: "0 2px 20px rgba(10,37,64,0.35)" }}
                >
                    Why we built it, how it works, and what your ledger looks like once you're inside.
                </p>

                <div className="mt-10">
                    <Link to="/" className="arcade-btn" data-variant="arts" data-testid="about-cta-signup">
                        Sign up for an account <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                {/* Branch tiles */}
                <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto text-left">
                    {[
                        { c: "#FF2052", t: "Sciences", d: "Logic · Data · Clinical" },
                        { c: "#FFC107", t: "Arts", d: "Design · Music · Form" },
                        { c: "#3EB489", t: "Trades", d: "Hands · Craft · Land" },
                        { c: "#6F00FF", t: "Humanities", d: "Words · Law · Mind" },
                        { c: "#1E88E5", t: "Business", d: "Ventures · Finance · Growth" },
                    ].map((b) => (
                        <div
                            key={b.t}
                            className="crystal rounded-2xl p-3"
                            style={{ borderColor: b.c }}
                        >
                            <div
                                className="w-3 h-3 rounded-full mb-2"
                                style={{ background: b.c, boxShadow: `0 0 10px ${b.c}` }}
                            />
                            <div className="font-display font-bold text-slate-900 text-sm">{b.t}</div>
                            <div className="text-[11px] text-slate-600">{b.d}</div>
                        </div>
                    ))}
                </div>

                {leaderboard.length > 0 && (
                    <div className="mt-14 max-w-2xl mx-auto w-full" data-testid="about-referral-leaderboard">
                        <div className="flex items-center justify-center gap-2 mb-3">
                            <Trophy className="w-4 h-4 text-arts" />
                            <p className="text-white/90 font-display font-bold tracking-tight uppercase text-xs">
                                Top Inviters — Explorers filling the Atlas
                            </p>
                        </div>
                        <div className="crystal rounded-3xl p-4 sm:p-5">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {leaderboard.map((row, i) => (
                                    <a
                                        href={`/u/${row.username}`}
                                        key={row.username}
                                        className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/70 transition-colors"
                                        data-testid={`leaderboard-row-${i}`}
                                    >
                                        <span
                                            className={`font-display font-black text-lg w-6 text-center ${
                                                i === 0
                                                    ? "text-arts"
                                                    : i === 1
                                                    ? "text-humanities"
                                                    : i === 2
                                                    ? "text-trades"
                                                    : "text-slate-500"
                                            }`}
                                        >
                                            {i + 1}
                                        </span>
                                        <span className="flex-1 min-w-0 truncate">
                                            <span className="font-display font-bold text-slate-900 text-sm">
                                                {row.name}
                                            </span>
                                            <span className="text-xs text-slate-500 ml-1.5">
                                                @{row.username}
                                            </span>
                                        </span>
                                        <span className="flex-shrink-0 flex items-center gap-1 chip bg-slate-900/5 text-slate-900 text-[11px]">
                                            <Users className="w-3 h-3" /> {row.referrals}
                                        </span>
                                    </a>
                                ))}
                            </div>
                            <p className="text-[11px] text-slate-500 text-center mt-3">
                                Bring a friend, both earn a free month. Stackable.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <AboutAtlas />
            <HowItWorks />

            {/* Final CTA */}
            <section className="relative z-10 px-6 py-20 text-center">
                <h2
                    className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white leading-none"
                    style={{ textShadow: "0 4px 30px rgba(10,37,64,0.4)" }}
                >
                    Ready to start filling your ledger?
                </h2>
                <div className="mt-8 flex flex-wrap gap-4 justify-center">
                    <Link
                        to="/"
                        className="arcade-btn"
                        data-variant="arts"
                        data-testid="about-cta-signup-bottom"
                    >
                        Sign up for an account <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                        to="/"
                        className="arcade-btn"
                        data-testid="about-cta-signin-bottom"
                    >
                        Sign in
                    </Link>
                </div>
            </section>
        </div>
    );
}
